import React from 'react';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import Pagination from '@prisjakt/ui-pagination';
import { ScrollElement, ScrollLink } from '@prisjakt/ui-scroll';
import CampaignItem from '@prisjakt/ui-campaign-item';
import Dropdown from '@prisjakt/ui-dropdown';
import { colors } from '@prisjakt/ui-base';
import SearchIcon from 'mdi-react/MagnifyIcon';
import T from '@prisjakt/ui-translate';
import PropTypes from 'prop-types';

import MatchMedia from '../../../../components/match-media';
import Placeholder from '../../../../components/placeholder';

import withElastic from '../data/with-elastic';
import queryBuilder from '../../query';
import actions from '../../actions';
import { trackSortOrderChange } from '../../tracking';
import Search from '../search';

import * as S from './styles';

const DEFAULT_SIZE = 24;
const NUMBER_OF_EMPTY_ITEMS = 4;
const DEFAULT_SORT = 'rank|desc';
const RATING_OPTION_KEYS = ['productRating', 'storeRating'];

const buildQuery = (state, props) => {
  const [sort, order] = (props.router.sort || DEFAULT_SORT).split('|');
  const offset = props.router.offset || 0;
  const { categories, query, filterRanges } = props.router;
  const { size } = props;
  let stores = props.router.stores;

  if (props.stores && props.stores.length) {
    // Stores can come both from the url or as props. Props have the higher priority atm in order to prevent weird behavior.
    stores = props.stores;
  }

  const body = {
    _source: {
      include: [
        'price.display.*',
        'price.diff_percentage',
        'price.days_ago_compare',
        'price.offer',
        'price.offer_incl_shipping',
        'product.id',
        'product.semantic_id',
        'product.web_uri',
        'product.mobile_web_uri',
        'product.name',
        'product.media',
        'product.price.count',
        'product.statistics.sparklines',
        'product.category.name',
        'product.user_reviews.rating',
        'product.user_reviews.count',
        'brand.logo',
        'brand.name',
        'store.name',
        'store.id',
        'store.logo',
      ],
    },
    from: offset,
    size: size || DEFAULT_SIZE,
  };

  const selectionGroups = [];

  // Use the score first if the current sort is relevance
  body.sort =
    sort === 'relevance'
      ? ['_score']
      : [
          {
            [sort]: {
              order: order || 'asc',
              unmapped_type:
                sort === 'product.name.keyword' ? 'keyword' : 'long',
            },
          },
          '_score',
        ];

  if (sort === DEFAULT_SORT.split('|')[0]) {
    body.sort.push({ 'product.popularity.total': { unmapped_type: 'long' } });
  }

  if (categories.length) {
    selectionGroups.push({
      bool: {
        should: categories.map(value => ({
          term: { 'product.category.path.id': value },
        })),
      },
    });
  }

  if (stores) {
    selectionGroups.push({
      bool: {
        should: stores.map(value => ({
          term: { 'store.id': value },
        })),
      },
    });
  }

  body.query = {
    bool: {
      must: selectionGroups,
    },
  };

  body.query.bool.filter = { bool: { must: [] } };
  props.rangeOptions.forEach(option => {
    if (filterRanges[option.key]) {
      const filter = { range: {} };
      filter.range[option.field] = {
        gte: RATING_OPTION_KEYS.includes(option.key)
          ? filterRanges[option.key].from * 2 // Ratings are between 1-10
          : filterRanges[option.key].from,
        lte: RATING_OPTION_KEYS.includes(option.key)
          ? filterRanges[option.key].to * 2 // Ratings are between 1-10
          : filterRanges[option.key].to,
      };
      body.query.bool.filter.bool.must.push(filter);
    }
  });

  if (query) {
    body.query.bool.must = body.query.bool.must.concat(queryBuilder(query));
  }

  return body;
};

const buildProductProps = (_source, renderMode, market) => {
  let link = market.useClassicUrls
    ? _source.product.web_uri.split(market.domain)[1] // [<siteDomain>, '/product.php?p=xyz']
    : _source.product.semantic_id;

  if (renderMode === 'desktop') {
    link = _source.product.web_uri.split(market.domain)[1];
  }

  if (renderMode === 'native') {
    link = _source.product.web_uri;
  }

  const product = {
    id: _source.product.id,
    name: _source.product.name,
    rating: _source.product.user_reviews.rating,
    count: _source.product.user_reviews.count,
    price: _source.price.offer,
    link,
    mode: 'grid',
    category: {
      name: _source.product.category.name,
    },
  };

  product.image = _source.product.media.product_images.first
    ? _source.product.media.product_images.first[280]
    : null;

  return product;
};

const getEmptyItems = () =>
  new Array(NUMBER_OF_EMPTY_ITEMS)
    .fill()
    .map((item, index) => index)
    .map(item => (
      <div key={item} className="pj-ui-product-list-item--grid empty" />
    ));

class Deals extends React.Component {
  constructor() {
    super();
    this.state = {
      showSearchField: false,
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.props.data !== nextProps.data ||
      this.props.fetching !== nextProps.fetching ||
      this.state.showSearchField !== nextState.showSearchField
    );
  }

  getFullSearchLink(query, mobile) {
    switch (this.context.renderMode) {
      case 'responsive':
      case 'beta':
        return `/search?q=${encodeURIComponent(query)}`;
      case 'desktop':
        if (mobile) {
          // Very ugly code to make sure search is directed to mobile site. window is safe to use here
          // as long as this is client side rendered
          return `https://m.${window.location.hostname.replace(
            'www.',
            ''
          )}/search/${encodeURIComponent(query)}`;
        }

        return `/#rparams=ss=${encodeURIComponent(query)}`;
      default:
        return '';
    }
  }

  getStatisticsLink(_source, mobile) {
    switch (this.context.renderMode) {
      case 'desktop':
        if (mobile) {
          return _source.product.statistics
            ? `${_source.product.mobile_web_uri}/statistics`
            : undefined;
        }

        return _source.product.statistics
          ? _source.product.statistics.web_uri
          : undefined;
      default:
        return undefined;
    }
  }

  static addStickyOffset() {
    if (window.innerWidth < 640) {
      return 50;
    }

    return 70;
  }

  render() {
    const { t, renderMode, market } = this.context;
    const {
      router,
      size,
      data = { hits: { total: 0, hits: [] } },
    } = this.props;

    let options = [
      'rank|desc',
      'product.name.keyword',
      'product.popularity.total',
      'price.offer|asc',
      'price.offer|desc',
      'product.user_reviews.rating|desc',
      'price.diff_percentage|desc',
    ];

    // Only show the relevance option if it should be used
    if (router.sort === 'relevance') {
      options.push('relevance');
    }

    options = options.map(val => ({
      name: t('campaigns', `sort.${val}`),
      value: val,
    }));

    const currentSort = options.find(item => router.sort === item.value) || {
      name: t('campaigns', `sort.${DEFAULT_SORT}`),
      value: DEFAULT_SORT,
    };

    // Remove currently selected option from the list
    options = options.filter(option => option.value !== currentSort.value);

    const pageInfo = {
      offset: router.offset,
      limit: size,
      hasNext: size + router.offset < data.hits.total,
      hasPrevious: !!router.offset,
    };

    const advertSpacing = { top: '10px', bottom: '10px' };

    const emptyItems = getEmptyItems();

    return (
      <S.Container>
        <MatchMedia
          debounce={1000}
          serverSideRender={{ isMobile: false }}
          matches={[
            {
              mediaQuery: '(max-width: 640px)',
              values: { isMobile: true },
            },
            {
              mediaQuery: '(min-width: 640px) and (max-width: 1024px)',
              values: { isTablet: true },
            },
            {
              mediaQuery: '(min-width: 1024px)',
              values: { isDesktop: true },
            },
          ]}
        >
          {({ isMobile, isTablet }) => {
            const productCountText = t(
              'campaigns',
              'deals.n-products-subtitle',
              {
                n: data.hits.total,
              }
            );
            const headerTitle =
              isMobile || isTablet ? '' : t('campaigns', 'products.title');
            const headerSubtitle = isMobile || isTablet ? '' : productCountText;
            const dropdownTitle =
              isMobile || isTablet ? '' : t('common', 'sort.title');

            return (
              <ScrollElement name="Campaigns">
                <S.Panel>
                  <Panel
                    header={
                      <React.Fragment>
                        <PanelHeader
                          title={headerTitle}
                          subtitle={headerSubtitle}
                          divider={isMobile || isTablet ? 'both' : 'none'}
                          margin="bottom"
                        >
                          <Dropdown
                            title={dropdownTitle}
                            options={options}
                            selected={currentSort.name}
                            onSelect={option => {
                              trackSortOrderChange(option.value);
                              this.props.onChange({
                                id: 'sort',
                                value: option.value,
                              });
                            }}
                          />
                          {isMobile || isTablet ? (
                            <button
                              type="button"
                              onClick={() => {
                                this.setState(prevState => ({
                                  showSearchField: !prevState.showSearchField,
                                }));
                              }}
                            >
                              <SearchIcon size={28} color={colors.lightBlue1} />
                            </button>
                          ) : null}
                        </PanelHeader>

                        {isMobile || isTablet ? (
                          <React.Fragment>
                            <S.SearchField show={this.state.showSearchField}>
                              <Search
                                query={router.query}
                                campaignName={this.props.campaignName}
                                onSearch={item => this.props.onChange(item)}
                              />
                            </S.SearchField>
                            <S.ProductCount>{productCountText}</S.ProductCount>
                          </React.Fragment>
                        ) : null}
                      </React.Fragment>
                    }
                    layoutMode="grid"
                    lines
                    spacing={false}
                    footer={
                      <ScrollLink
                        to="CampaignHeaderAd"
                        shouldScroll={({ tagName }) => tagName === 'A'}
                        animate={false}
                        calculateOffset={Deals.addStickyOffset}
                      >
                        <Pagination
                          pageInfo={pageInfo}
                          onNext={this.props.campaignPagination}
                          onPrev={this.props.campaignPagination}
                          onReset={this.props.campaignPagination}
                        />
                      </ScrollLink>
                    }
                  >
                    {this.props.fetching && (
                      <Placeholder
                        height={450}
                        amount={12}
                        rows={8}
                        mediaPosition={4}
                      />
                    )}

                    <S.Ad
                      placement="dubbelpanorama2"
                      min={0}
                      max={639}
                      spacing={advertSpacing}
                      mobilePosition
                    />

                    <S.Ad
                      scalable
                      scaleBase=".pj-ui-panel"
                      scaleOrigin="left center"
                      placement="panorama_extra"
                      min={640}
                      max={10000}
                      spacing={advertSpacing}
                      panoramaPosition
                    />

                    {!this.props.fetching && data.hits.hits.length === 0 && (
                      <S.Empty>
                        <T
                          domain="campaigns"
                          fullSearchLink={this.getFullSearchLink(
                            this.props.router.query,
                            isMobile
                          )}
                        >
                          item.no-results.md
                        </T>
                      </S.Empty>
                    )}

                    <React.Fragment>
                      {!this.props.fetching &&
                        data.hits.hits.map(({ _source, _id }) => (
                          <CampaignItem
                            key={_id}
                            onClick={() => {
                              this.props.onCampaignItemClick();
                              this.props.campaignItemClick(_source);
                            }}
                            diffPercentage={Math.round(
                              _source.price.diff_percentage
                            )}
                            product={buildProductProps(
                              _source,
                              renderMode,
                              market
                            )}
                            freeShipping={
                              _source.price.offer ===
                              _source.price.offer_incl_shipping
                                ? t('campaigns', 'item.free-shipping')
                                : ''
                            }
                            store={
                              _source.store && {
                                id: _source.store.id,
                                name: _source.store.name,
                                hasLogo: !!_source.store.logo,
                              }
                            }
                            previousPriceText={t(
                              'campaigns',
                              'deals.price-n-days-ago',
                              {
                                /* The date that days_ago_compare is based on is taken from just after midnight,
                                      so from a user perspective it makes more sense to say that the comparison is
                                      with the price from the day *before* that. A bit confusing, but that's how
                                      it seems to work... */
                                n: _source.price.days_ago_compare + 1,
                                price: _source.price.display.previousPrice,
                              }
                            )}
                            soldByXNrOfStores={t(
                              'campaigns',
                              'item.sold-by-n-stores',
                              {
                                n: _source.product.price.count,
                              }
                            )}
                            statistics={{
                              label: t('campaigns', 'item.price-trend'),
                              values:
                                _source.product.statistics &&
                                _source.product.statistics.sparklines &&
                                _source.product.statistics.sparklines
                                  .lowest_price &&
                                _source.product.statistics.sparklines
                                  .lowest_price.values
                                  ? _source.product.statistics.sparklines
                                      .lowest_price.values
                                  : [],
                              link: this.getStatisticsLink(_source, isMobile),
                            }}
                            externalLink={[
                              'desktop',
                              'mobile',
                              'native',
                            ].includes(renderMode)}
                            hasDivider
                          />
                        ))}
                      {emptyItems}
                    </React.Fragment>
                  </Panel>
                </S.Panel>
              </ScrollElement>
            );
          }}
        </MatchMedia>
      </S.Container>
    );
  }
}

const { string, func, object, shape, arrayOf, bool, number } = PropTypes;

Deals.propTypes = {
  data: object, // eslint-disable-line react/forbid-prop-types
  router: shape({
    location: shape({
      pathname: string,
      search: string,
    }),
  }).isRequired,
  rangeOptions: arrayOf(
    shape({
      key: string,
      field: string,
      translation: string,
      showBars: bool,
    })
  ).isRequired,
  onChange: func.isRequired,
  campaignItemClick: func.isRequired,
  campaignPagination: func.isRequired,
  onCampaignItemClick: func.isRequired,
  fetching: bool,
  stores: PropTypes.arrayOf(number),
  size: number,
  campaignName: string,
};

Deals.defaultProps = {
  data: undefined,
  fetching: false,
  size: DEFAULT_SIZE,
  stores: [],
  campaignName: '',
};

Deals.contextType = AppContext;

export default withElastic(buildQuery)(connect(null, actions)(Deals));
